import MonsterReferenceTable from "../rules/MonsterReferenceTable";
import CursedScrollMonsterReferenceTable from "../rules/CursedScrollMonsterReferenceTable";
import { MonsterDataType } from "../rules/models/MonsterData";
import Dice from "./Dice";

/**
 * @class MonsterSearchOptions
 * @description This class provides a set of search options for filtering monsters in the MonsterReferenceTable.
 */
class MonsterSearchOptions {
    public includeCSMonsters: boolean = false;
    public levelLower: number = -1;
    public levelUpper: number = -1;
    public biomeTags: string[] = [];
    public moveTags: string[] = [];
    public attackTags: string[] = [];
    public typeTags: string[] = [];
    public effectTags: string[] = [];
    public traitTags: string[] = [];
    public keywords: string = '';

}

/**
 * @class MonsterManual
 * @description This class provides methods to search the MonsterReferenceTable and CursedScrollMonsterReferenceTable. It provides methods to get all available tags for biomes, moves, attacks, and types.
 */
class MonsterManual {

    /**
     * @description This method retrieves all unique biome tags from the MonsterReferenceTable and returns them as an array of strings.
     * @returns {string[]} An array of all available biome tags in the MonsterReferenceTable.
     */
    public static getAllBiomeTags(): string[] {
        // console.log("GET ALL BIOME TYPES");

        let biomeTypes: string[] = [];
        MonsterReferenceTable.entries.forEach((m) => {
            const biomeArray = m.biome.split(",");
            biomeTypes = biomeTypes.concat(biomeArray);
        });

        let result = [...new Set(biomeTypes)].sort((a, b) => a.localeCompare(b));
        result = result.filter((r) => { return r !== '' });
        result = result.filter((r) => { return r !== '*' });

        // console.log(`getAllBiomeTypes: ${result}`);
        return result;
    }

    /**
     * @description This method retrieves all unique movement tags from the MonsterReferenceTable and returns them as an array of strings.
     * @returns {string[]} An array of all available movement tags in the MonsterReferenceTable.
     */
    public static getAllMoveTags(): string[] {
        // console.log("GET ALL MOVE TYPES");

        // const start = Date.now();

        const moveTypes = MonsterReferenceTable.entries.map((m) => {
            return m.move;
        });
        let result = [...new Set(moveTypes)].sort((a, b) => a.localeCompare(b));
        result = result.filter((r) => { return r !== '' });

        // const end = Date.now();
        // console.log(`getAllMoveTypes: ${result}`);
        // console.log(`getAllMoveTypes start: ${start}`);
        // console.log(`getAllMoveTypes end: ${end}`);
        // console.log(`getAllMoveTypes duaation: ${end - start}`);

        return result;
    }

    /**
     * @description This method retrieves all unique attack tags from the MonsterReferenceTable and returns them as an array of strings.
     * @returns {string[]} An array of all available attack tags in the MonsterReferenceTable.
     */
    public static getAllAttackTags(): string[] {
        // console.log("GET ALL ATTACK TYPES");

        let attackTypes: string[] = [];
        MonsterReferenceTable.entries.forEach((m) => {
            const attackArray = m.attack.split(",");
            attackTypes = attackTypes.concat(attackArray);
        });

        let result = [...new Set(attackTypes)].sort((a, b) => a.localeCompare(b));
        result = result.filter((r) => { return r !== '' });

        // console.log(`getAllAttackTypes: ${result}`);
        return result;
    }

    /**
     * @description This method retrieves all unique type tags from the MonsterReferenceTable and returns them as an array of strings.
     * @returns {string[]} An array of all available type tags in the MonsterReferenceTable.
     */
    public static getAllTypeTags(): string[] {
        // console.log("GET ALL TAG TYPES");

        let tagTypes: string[] = [];
        MonsterReferenceTable.entries.forEach((m) => {
            const tagArray = m.tags.split(",");
            tagTypes = tagTypes.concat(tagArray);
        });

        let result = [...new Set(tagTypes)].sort((a, b) => a.localeCompare(b));
        result = result.filter((r) => { return r !== '' });

        // console.log(`getAllTagTypes: ${result}`);
        return result;
    }

    /**
     * @description This method retrieves all unique effect tags from the MonsterReferenceTable and returns them as an array of strings.
     * @returns {string[]} An array of all available effect tags in the MonsterReferenceTable.
     */
    public static getAllEffectTags(): string[] {
        // console.log("GET ALL EFFECT TYPES");
        let effectTypes: string[] = [];
        MonsterReferenceTable.entries.forEach((m) => {
            const effectArray = m.effect.split(",");
            effectTypes = effectTypes.concat(effectArray);
        });

        let result = [...new Set(effectTypes)].sort((a, b) => a.localeCompare(b));
        result = result.filter((r) => { return r !== '' });

        // console.log(`getAllEffectTypes: ${result}`);
        return result;  
    }

    /**
     * @description This method retrieves all unique trait tags from the MonsterReferenceTable and returns them as an array of strings.
     * @returns {string[]} An array of all available trait tags in the MonsterReferenceTable.
     */
    public static getAllTraitTags(): string[] {
        // console.log("GET ALL TRAIT TYPES");
        let traitTypes: string[] = [];
        MonsterReferenceTable.entries.forEach((m) => {
            const tagArray = m.trait.split(",");
            traitTypes = traitTypes.concat(tagArray);
        });

        let result = [...new Set(traitTypes)].sort((a, b) => a.localeCompare(b));
        result = result.filter((r) => { return r !== '' });

        // console.log(`getAllTraitTypes: ${result}`);
        return result;
    }


    /**
     * @description This method filters the monsters in the MonsterReferenceTable based on the specified level range.
     * @param {MonsterDataType[]} monsters - The array of monsters to filter.
     * @param {number} lowLevel - The lower level bound for filtering.
     * @param {number} highLevel - The upper level bound for filtering.
     * @returns {MonsterDataType[]} An array of monsters that fall within the specified level range.
     **/
    private static getMonstersByLevel(monsters: MonsterDataType[], lowLevel: number, highLevel: number): MonsterDataType[] {

        let result: MonsterDataType[] = [];

        if (lowLevel > -1 && highLevel > -1) {
            // both lower and upper bounds
            result = monsters.filter((m) => {
                if (m.level === "*") {
                    return true;
                }
                const mLevel: number = +(m.level);
                return (mLevel >= lowLevel && mLevel <= highLevel);
            });
        }
        else if (lowLevel > -1) {
            // lower bound only
            result = monsters.filter((m) => {
                if (m.level === "*") {
                    return true;
                }
                const mLevel: number = +(m.level);
                return (mLevel >= lowLevel);
            });
        }
        else if (highLevel > -1) {
            // uppber bound only
            result = monsters.filter((m) => {
                if (m.level === "*") {
                    return true;
                }
                const mLevel: number = +(m.level);
                return (mLevel <= highLevel);
            });
        }
        else {
            // no levels specified
            result = monsters;
        }

        return result;
    }

    /**
     * @description This method filters the monsters in the MonsterReferenceTable based on the specified movement types.
     * @param {MonsterDataType[]} monsters - The array of monsters to filter.
     * @param {string[]} moveTypes - The array of movement types to filter by.
     * @returns {MonsterDataType[]} An array of monsters that match the specified movement types.
     */
    private static filterMonstersByMovement(monsters: MonsterDataType[], moveTypes: string[]): MonsterDataType[] {
        // console.log(`*** filterMonstersByMovement`);
        // console.log(`*** filterMonstersByMovement monsters.length = ${monsters.length}`);
        // console.log(`*** filterMonstersByMovement moveTypes.length = ${moveTypes.length}`);
        // console.log(`*** filterMonstersByMovement moveTypes = ${moveTypes}`);

        if (moveTypes.length === 0) {
            return monsters;
        }

        let result = monsters.filter((m) => {
            const monsterMoves = m.move.split(",");
            let monsterResult = false;
            moveTypes.forEach((moveType) => {
                return monsterResult = monsterResult || (monsterMoves.indexOf(moveType) > -1);
            });
            return monsterResult;
        });
        return result;
    }

    /**
     * @description This method filters the monsters in the MonsterReferenceTable based on the specified keywords.
     * @param {MonsterDataType[]} monsters - The array of monsters to filter.
     * @param {string} keywords - The keywords to filter by.
     * @returns {MonsterDataType[]} An array of monsters that match the specified keywords.
     */
    private static filterMonstersByKeyword(monsters: MonsterDataType[], keywords: string): MonsterDataType[] {
        // console.log(`*** filterMonstersByMovement`);
        // console.log(`*** filterMonstersByMovement monsters.length = ${monsters.length}`);
        // console.log(`*** filterMonstersByMovement moveTypes.length = ${moveTypes.length}`);
        // console.log(`*** filterMonstersByMovement moveTypes = ${moveTypes}`);

        if (keywords.trim() === '') {
            return monsters;
        }

        let keywordList = keywords.toLowerCase().trim().split(" ");
        // console.log(`filterMonstersByKeyword.keywordList.lenght = ${keywordList.length}`);
        keywordList = keywordList.filter((k) => { return k.trim() !== ''; });
        // console.log(`filterMonstersByKeyword.keywordList = ${keywordList}`);
        keywordList.map((k) => k.trim());

        // console.log(`filterMonstersByKeyword.keywordList = ${keywordList}`);


        let result = monsters.filter((m) => {
            let monsterResult = false;
            keywordList.forEach((keyword) => {
                return monsterResult = monsterResult || m.name.toLowerCase().includes(keyword) || m.statblock.toLowerCase().includes(keyword);
            });
            return monsterResult;
        });
        return result;
    }

    /**
     * @description This method filters the monsters in the MonsterReferenceTable based on the specified attack types.
     * @param {MonsterDataType[]} monsters - The array of monsters to filter.
     * @param {string[]} attackTypes - The array of attack types to filter by.
     * @returns {MonsterDataType[]} An array of monsters that match the specified attack types.
     */
    private static filterMonstersByAttack(monsters: MonsterDataType[], attackTypes: string[]): MonsterDataType[] {
        // console.log(`*** filterMonstersByAttack`);
        // console.log(`*** filterMonstersByAttack monsters.length = ${monsters.length}`);
        // console.log(`*** filterMonstersByAttack attackTypes.length = ${attackTypes.length}`);
        // console.log(`*** filterMonstersByAttack attackTypes = ${attackTypes}`);

        if (attackTypes.length === 0) {
            return monsters;
        }

        let result = monsters.filter((m) => {
            const monsterAttacks = m.attack.split(",");
            let monsterResult = false;
            attackTypes.forEach((attackType) => {
                return monsterResult = monsterResult || (monsterAttacks.indexOf(attackType) > -1);
            });
            return monsterResult;
        });
        return result;
    }

    /**
     * @description This method filters the monsters in the MonsterReferenceTable based on the specified biome tags.
     * @param {MonsterDataType[]} monsters - The array of monsters to filter.
     * @param {string[]} biomeTags - The array of biome tags to filter by.
     * @returns {MonsterDataType[]} An array of monsters that match the specified biome tags.
     */
    private static filterMonstersByBiome(monsters: MonsterDataType[], biomeTags: string[]): MonsterDataType[] {
        // console.log(`*** filterMonstersByBiome`);
        // console.log(`*** filterMonstersByBiome monsters.length = ${monsters.length}`);
        // console.log(`*** filterMonstersByBiome biomeTags.length = ${biomeTags.length}`);
        // console.log(`*** filterMonstersByBiome biomeTags = ${biomeTags}`);

        if (biomeTags.length === 0) {
            return monsters;
        }

        let result = monsters.filter((m) => {
            const monsterBiomes = m.biome.split(",");
            let monsterResult = (monsterBiomes.indexOf("*") > -1 ? true : false);
            biomeTags.forEach((biomeTag) => {
                return monsterResult = monsterResult || (monsterBiomes.indexOf(biomeTag) > -1);
            });
            return monsterResult;
        });
        return result;
    }

    /**
     * @description This method filters the monsters in the MonsterReferenceTable based on the specified biome tags.
     * @param {MonsterDataType[]} monsters - The array of monsters to filter.
     * @param {string[]} biomeTags - The array of biome tags to filter by.
     * @returns {MonsterDataType[]} An array of monsters that match the specified biome tags.
     */
    private static filterMonstersByType(monsters: MonsterDataType[], typeTags: string[]): MonsterDataType[] {
        // console.log(`*** filterMonstersByBiome`);
        // console.log(`*** filterMonstersByBiome monsters.length = ${monsters.length}`);
        // console.log(`*** filterMonstersByBiome biomeTags.length = ${biomeTags.length}`);
        // console.log(`*** filterMonstersByBiome biomeTags = ${biomeTags}`);

        if (typeTags.length === 0) {
            return monsters;
        }

        let result = monsters.filter((m) => {
            const monsterTypes = m.tags.split(",");
            let monsterResult = false;
            typeTags.forEach((typeTag) => {
                return monsterResult = monsterResult || (monsterTypes.indexOf(typeTag) > -1);
            });
            return monsterResult;
        });
        return result;
    }

    /**
     * @description This method filters the monsters in the MonsterReferenceTable based on the specified trait tags.
     * @param {MonsterDataType[]} monsters - The array of monsters to filter.
     * @param {string[]} traitTags - The array of trait tags to filter by.
     * @returns {MonsterDataType[]} An array of monsters that match the specified trait tags.
     */
    private static filterMonstersByTrait(monsters: MonsterDataType[], traitTags: string[]): MonsterDataType[] {
        // console.log(`*** filterMonstersByBiome`);
        // console.log(`*** filterMonstersByBiome monsters.length = ${monsters.length}`);
        // console.log(`*** filterMonstersByBiome biomeTags.length = ${biomeTags.length}`);
        // console.log(`*** filterMonstersByBiome biomeTags = ${biomeTags}`);

        if (traitTags.length === 0) {
            return monsters;
        }

        let result = monsters.filter((m) => {
            const monsterTraits = m.trait.split(",");
            let monsterResult = false;
            traitTags.forEach((traitTag) => {
                return monsterResult = monsterResult || (monsterTraits.indexOf(traitTag) > -1);
            });
            return monsterResult;
        });
        return result;
    }

    /**
     * @description This method filters the monsters in the MonsterReferenceTable based on the specified effect tags.
     * @param {MonsterDataType[]} monsters - The array of monsters to filter.
     * @param {string[]} effectTags - The array of effect tags to filter by.
     * @returns {MonsterDataType[]} An array of monsters that match the specified effect tags.
     */
    private static filterMonstersByEffect(monsters: MonsterDataType[], effectTags: string[]): MonsterDataType[] {
        // console.log(`*** filterMonstersByBiome`);
        // console.log(`*** filterMonstersByBiome monsters.length = ${monsters.length}`);
        // console.log(`*** filterMonstersByBiome biomeTags.length = ${biomeTags.length}`);
        // console.log(`*** filterMonstersByBiome biomeTags = ${biomeTags}`);

        if (effectTags.length === 0) {
            return monsters;
        }

        let result = monsters.filter((m) => {
            const monsterEffects = m.effect.split(",");
            let monsterResult = false;
            effectTags.forEach((effectTag) => {
                return monsterResult = monsterResult || (monsterEffects.indexOf(effectTag) > -1);
            });
            return monsterResult;
        });
        return result;
    }



    /**
     * @description This method searches the MonsterReferenceTable and CursedScrollMonsterReferenceTable based on the specified search options.
     * @param {MonsterSearchOptions} searchOptions - The search options to use for the search.
     * @returns {MonsterDataType[]} An array of MonsterDataType objects that match the search criteria.
     */
    public static searchMonsters(searchOptions: MonsterSearchOptions): MonsterDataType[] {

        let monsterPool: MonsterDataType[] = MonsterReferenceTable.entries;
        if (searchOptions.includeCSMonsters) {
            monsterPool = monsterPool.concat(CursedScrollMonsterReferenceTable.entries);
        }


        let levelAndBiome = MonsterManual.getMonstersByLevel(monsterPool, searchOptions.levelLower, searchOptions.levelUpper);
        levelAndBiome = MonsterManual.filterMonstersByBiome(levelAndBiome, searchOptions.biomeTags);

        levelAndBiome = MonsterManual.filterMonstersByKeyword(levelAndBiome, searchOptions.keywords);

        // return levelAndBiome;

        // let results = MonsterManual.getMonstersByLevel(searchOptions.levelLower, searchOptions.levelUpper);

        // results = MonsterManual.filterMonstersByMovement(results, searchOptions.moveTags);
        // results = MonsterManual.filterMonstersByAttack(results, searchOptions.attackTags);
        // return results;

        let byMove = [] as MonsterDataType[];
        if (searchOptions.moveTags.length > 0) {
            byMove = MonsterManual.filterMonstersByMovement(levelAndBiome, searchOptions.moveTags);
        }

        let byAttack = [] as MonsterDataType[];
        if (searchOptions.attackTags.length > 0) {
            byAttack = MonsterManual.filterMonstersByAttack(levelAndBiome, searchOptions.attackTags);
        }

        let byType = [] as MonsterDataType[];
        if (searchOptions.typeTags.length > 0) {
            byType = MonsterManual.filterMonstersByType(levelAndBiome, searchOptions.typeTags);
        }

        let byEffect = [] as MonsterDataType[];
        if (searchOptions.effectTags.length > 0) {
            byEffect = MonsterManual.filterMonstersByEffect(levelAndBiome, searchOptions.effectTags);
        }
        let byTrait = [] as MonsterDataType[];
        if (searchOptions.traitTags.length > 0) {
            byTrait = MonsterManual.filterMonstersByTrait(levelAndBiome, searchOptions.traitTags);
        }

        // console.log(`attackTags = ${searchOptions.attackTags}`);
        // console.log(`moveTags = ${searchOptions.moveTags}`);
        // console.log(`typeTags = ${searchOptions.typeTags}`);

        if ((searchOptions.attackTags.length
            + searchOptions.moveTags.length
            + searchOptions.typeTags.length
            + searchOptions.effectTags.length
            + searchOptions.traitTags.length) > 0) {
            // console.log('at least one optional tag');

            let result = byMove.concat(byAttack).concat(byType).concat(byEffect).concat(byTrait);
            result = [...new Set(result)].sort((a, b) => a.name.localeCompare(b.name));
            return result;

        }
        else {
            // console.log('no optional tags');
            // console.log("levelAndBiome");
            // console.log(levelAndBiome.length);
            levelAndBiome = [...levelAndBiome].sort((a, b) => a.name.localeCompare(b.name));
            return levelAndBiome;
        }
    }


    /**
     * @description This method searches the MonsterReferenceTable and CursedScrollMonsterReferenceTable based on the specified search options. It requires all the specified tags to be present in each of the resulting monsters. This is different from @see MonsterManual.searchMonsters, which allows for any of the tags to be present.
     * Filtered monsters will
     *  - be within the level range, if specified,
     *  - contain one of the keywords, if specified,
     *  - be tagged with one of the environments selected,
     *  - be tagged with one of the types selected,
     *  - be tagged with one of the movements selected, and
     *  - be tagged with one of the attacks selected.
     *  - be tagged with one of the effects selected.
     *  - be tagged with one of the traits selected.
     * @param {MonsterSearchOptions} searchOptions - The search options to use for the search.
     * @returns {MonsterDataType[]} An array of MonsterDataType objects that match the search criteria.
     */
    public static searchMonstersRestrictive(searchOptions: MonsterSearchOptions): MonsterDataType[] {
        // gather the moster entries to be searched.
        let monsterPool: MonsterDataType[] = MonsterReferenceTable.entries;
        if (searchOptions.includeCSMonsters) {
            monsterPool = monsterPool.concat(CursedScrollMonsterReferenceTable.entries);
        }

        // reduce candidates by level and biome and keywords.
        let results = MonsterManual.getMonstersByLevel(monsterPool, searchOptions.levelLower, searchOptions.levelUpper);
        results = MonsterManual.filterMonstersByBiome(results, searchOptions.biomeTags);
        results = MonsterManual.filterMonstersByKeyword(results, searchOptions.keywords);

        // work thgrough the optional tags: Type, Movement, Attack
        // console.log(`searchMonstersRestrictive: searchOptions.typeTags: ${searchOptions.typeTags}`);
        // console.log(`searchMonstersRestrictive: searchOptions.moveTags: ${searchOptions.moveTags}`);
        // console.log(`searchMonstersRestrictive: searchOptions.attackTags: ${searchOptions.attackTags}`);
        // console.log(`searchMonstersRestrictive: searchOptions.effectTags: ${searchOptions.effectTags}`);
        // console.log(`searchMonstersRestrictive: searchOptions.traitTags: ${searchOptions.traitTags}`);
        if (searchOptions.typeTags.length > 0) {
            results = MonsterManual.filterMonstersByType(results, searchOptions.typeTags);
        }
        if (searchOptions.moveTags.length > 0) {
            results = MonsterManual.filterMonstersByMovement(results, searchOptions.moveTags);
        }
        if (searchOptions.attackTags.length > 0) {
            results = MonsterManual.filterMonstersByAttack(results, searchOptions.attackTags);
        }
        if (searchOptions.effectTags.length > 0) {
            results = MonsterManual.filterMonstersByEffect(results, searchOptions.effectTags);
        }
        if (searchOptions.traitTags.length > 0) {
            results = MonsterManual.filterMonstersByTrait(results, searchOptions.traitTags);
        }
        return results.sort((a, b) => (a.name).localeCompare(b.name));

    }


    /**
     * @description This method searches for a specified number of random monsters from the MonsterReferenceTable and CursedScrollMonsterReferenceTable based on the provided search options.
     * @param {MonsterSearchOptions} searchOptions - The search options to use for the search.
     * @param {number} nMonsters - The number of random monsters to search for.
     * @returns {MonsterDataType[]} An array of MonsterDataType objects that match the search criteria.
     */
    public static searchForNMonsters(searchOptions: MonsterSearchOptions, nMonsters: number): MonsterDataType[] {

        const monsterPool = MonsterManual.searchMonsters(searchOptions);
        let result = [] as MonsterDataType[];

        for (let m = 0; m < nMonsters; m++) {
            const m = Dice.dCustom(0, monsterPool.length - 1);
            // console.log(`searchForNMonsters = [${m}]`);
            result.push(monsterPool[m]);
        }

        return result;
    }




    /**
     * @description This method searches (restrictively) for a specified number of random monsters from the MonsterReferenceTable and CursedScrollMonsterReferenceTable based on the provided search options.
     * @param {MonsterSearchOptions} searchOptions - The search options to use for the search.
     * @param {number} nMonsters - The number of random monsters to search for.
     * @returns {MonsterDataType[]} An array of MonsterDataType objects that match the search criteria.
     */
    public static searchForNMonstersRestrictive(searchOptions: MonsterSearchOptions, nMonsters: number): MonsterDataType[] {

        const monsterPool = MonsterManual.searchMonstersRestrictive(searchOptions);
        let result = [] as MonsterDataType[];

        for (let m = 0; m < nMonsters; m++) {
            const m = Dice.dCustom(0, monsterPool.length - 1);
            // console.log(`searchForNMonsters = [${m}]`);
            result.push(monsterPool[m]);
        }

        return result;
    }


}

export { MonsterSearchOptions };
export default MonsterManual;
